import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { LoadingStatusByRoom, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { getActivePackagesByRoom, getCalendarsToBeFetchedByRoom, getMultiRoomPackageTotals, getIsLoadingByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MultiRoomOfferSelectRoomsButton from './MultiRoomOfferSelectRoomsButton'
import MultiRoomOfferAddToCartButton from './MultiRoomOfferAddToCartButton'
import MultiRoomOfferBookButton from './MultiRoomOfferBookButton'
import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { EmptyArray, groupBy, nonNullable } from 'lib/array/arrayUtils'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'LuxKit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import config from 'constants/config'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatCurrency from 'components/Common/FormatCurrency'
import { isRewardFareClass, type FareClassWithReward } from 'constants/flight'
import { computeRewardBundlePrice } from 'lib/maldivesCharter/classicRewardPricing'
import { FormattedMessage, useIntl } from 'react-intl'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import TextLink from 'LuxKit/TextLink'

const FooterContainer = styled(Pane)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

const InsufficientPointsBannerWrap = styled.div`
  ${mediaQueryUp.tablet} {
    max-width: ${rem(360)};
  }
`

interface Props {
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  checkIn?: string
  isFlightsSelected: boolean
  multiRoomPackages: PackageOptionViewByRoom
  multiRoomPackageTotals: MultiRoomPackageTotals
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  canRedeemLuxPlusBenefits: boolean
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  priceMode: OfferPagePriceMode
  priceIsLuxPlus: boolean
  charterBundleRoute?: App.CharterBundleRoute
  duration: number
  fareClass?: FareClassWithReward
}

function MultipleRoomsIndividualRoomsFooter({
  offer,
  rooms,
  checkIn,
  isFlightsSelected,
  multiRoomPackages,
  multiRoomPackageTotals,
  isLoadingByRoom,
  calendarsToBeFetchedByRoom,
  canRedeemLuxPlusBenefits,
  baseBnblPackageViews,
  priceMode,
  priceIsLuxPlus,
  charterBundleRoute,
  duration,
  fareClass,
}: Props) {
  const intl = useIntl()

  const limitedHoursLabel = intl.formatMessage({
    id: 'WwAw1OMtcLdeKlcUqvGX',
    defaultMessage: 'Our team are available 7 days a week from 9am to 7pm',
    description: 'Support availability message for regions without 24/7 support',
  })
  const fullHoursLabel = intl.formatMessage({
    id: '8JPALsyZTPzYnQudUPT6',
    defaultMessage: 'Our team are available 24 hours, 7 days',
    description: 'Support availability message for regions with 24/7 support',
  })

  const {
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    shouldDisplayValue,
  } = multiRoomPackageTotals

  const {
    totalFlightPrice: flightPrice,
    loading: flightPriceLoading,
    rewardAvailable,
    rewardSeatPointsTotal,
    rewardSeatCashCoPayTotal,
  } = useOfferPageFlightPrice()

  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice && !flightPriceLoading

  const hotelPlusFlightsPrice = hotelPrice + (flightPrice ?? 0)
  const hotelPlusFlightsMemberPrice = hotelMemberPrice ? hotelMemberPrice + (flightPrice ?? 0) : hotelMemberPrice
  const hotelPlusFlightsValue = hotelValue + (flightPrice ?? 0)

  const isRewardSelected = !!fareClass && isRewardFareClass(fareClass)
  const showRewardPrice = isRewardSelected
    && !!rewardAvailable
    && rewardSeatPointsTotal !== undefined
    && rewardSeatCashCoPayTotal !== undefined

  // Insufficient-points guard: when the user's reward booking requires more Société
  // points than they hold, surface the deficit + Buy points link and disable the
  // Select rooms / Book / Add-to-cart CTAs. Mirrors the cart-level guard.
  useLoyaltyAccount()
  const memberPointsBalance = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const rewardPointsDeficit = showRewardPrice && rewardSeatPointsTotal != null
    ? Math.max(0, rewardSeatPointsTotal - memberPointsBalance)
    : 0
  const hasInsufficientRewardPoints = !!memberId && rewardPointsDeficit > 0

  const showModal = useContext(ModalContext)
  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)

  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && hotelMemberPrice > 0 || missingCharterFlightPrice
  const isCapacityValid = Object.values(multiRoomPackages).every(pkg => pkg?.capacityValid)
  const isAllowBuyNowBookLater = Object.values(multiRoomPackages).every(pkg => pkg?.package.allowBuyNowBookLater)
  const showBNBL = offer.hasBuyNowBookLater && config.accommodation.hasBnbl && isCapacityValid && isAllowBuyNowBookLater && !charterBundleRoute && !shouldPreventMultipleRoomsBooking

  const isBookNowDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(multiRoomPackages).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, multiRoomPackages])

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration })
    ))
  }, [offer, multiRoomPackages, currentRegionCode])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !isFlightsSelected,
      isReward: isRewardSelected,
    }),
  ], [offer, isFlightsSelected, flightPrice, loyaltyHotelOptions, isRewardSelected])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const onOpenBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    offerPageDispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })

    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const selectedPackages = nonNullable(Object.values(multiRoomPackages))
    const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
    const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
      const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])!
      const numberOfRooms = groupedPackage[1].length

      return {
        packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
        numberOfRooms,
      }
    })

    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [dispatch, offerPageDispatch, multiRoomPackages, offer.id, baseBnblPackageViews])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const canShowPrice = hotelPrice > 0 && !missingCharterFlightPrice
  const activeHotelPrice = priceIsLuxPlus ? hotelMemberPrice : hotelPrice
  const discountPercentage = getValueOffPercent(hotelValue, activeHotelPrice)
  const shouldShowValue = shouldDisplayValue && hotelValue > activeHotelPrice
  const shouldShowDiscount = shouldShowValue && !offer.isDiscountPillHidden && discountPercentage > MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const nightsLabel = intl.formatMessage(
    {
      id: 'QPoaB3ITOoUJw1zHrXtk',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Night count for multi-room footer price caption on the offer detail page',
    },
    { count: duration },
  )
  const roomsLabel = intl.formatMessage(
    {
      id: 'k84rXa8cipLdDiOzGBvI',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Room count for multi-room footer price caption on the offer detail page',
    },
    { count: rooms.length },
  )
  const nightsSaleUnitsFlightsFromCaption = intl.formatMessage(
    {
      id: 'kdR6N51nwe52NIHOkSaZ',
      defaultMessage: '{nightsLabel}, {roomsLabel} + flights from',
      description:
        'Price caption in the multi-room footer when flights are selected, combining localized night and room counts with the flights label',
    },
    {
      nightsLabel,
      roomsLabel,
    },
  )
  const nightsSaleUnitsFromCaption = intl.formatMessage(
    {
      id: 'eZV-TO6RPOMgdJ2leHjy',
      defaultMessage: '{nightsLabel}, {roomsLabel} from',
      description:
        'Price caption in the multi-room footer when showing hotel-only pricing, combining localized night and room counts with the from label',
    },
    {
      nightsLabel,
      roomsLabel,
    },
  )

  const rewardPriceElement = showRewardPrice && isFlightsSelected && hasFlights ? (() => {
    const rewardPrice = computeRewardBundlePrice({
      hotelPrice,
      hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice : undefined,
      rewardPointsTotal: rewardSeatPointsTotal,
      rewardCashCoPayTotal: rewardSeatCashCoPayTotal,
    })
    const isMemberReward = rewardPrice.memberCash !== undefined
    const rewardCashHeadline = rewardPrice.memberCash ?? rewardPrice.cash
    return (
      <VerticalSpacer gap={4}>
        <Group direction="horizontal" gap={4} verticalAlign="baseline" wrap="wrap">
          <Heading variant="heading4" colour={isMemberReward ? 'lux-plus' : undefined}>
            <FormattedMessage
              id="w0gvQwxmTH3UKHo__CD9"
              defaultMessage="{points} + {cash}"
              description="Reward-seat bundle price on the multi-room offer page footer, showing the loyalty points portion and the cash co-pay portion combined as one heading."
              values={{
                points: <FormatLoyaltyPoints points={rewardSeatPointsTotal} abbreviated />,
                cash: <FormatCurrency value={rewardCashHeadline} format="roundedDollar" />,
              }}
            />
          </Heading>
          <Caption variant="medium" colour={isMemberReward ? 'lux-plus' : 'neutral-three'} weight="normal">
            <FormattedMessage
              id="RMUQ1N_t9AA8FtDmWyi6"
              defaultMessage="{unit}*"
              description="Sale unit suffix with asterisk appended after the reward bundle price heading on the multi-room offer page footer."
              values={{ unit: priceMode === 'perPerson' ? 'person' : 'total' }}
            />
          </Caption>
        </Group>
        {isMemberReward && (
          <Caption variant="medium" colour="neutral-two">
            <FormattedMessage
              id="Pv-MGsqhoCr6dQXM1I09"
              defaultMessage="Non-member <b>{points} + {cash}</b>"
              description="Non-member reward price caption shown below the LuxPlus member price on the multi-room offer page footer."
              values={{
                points: <FormatLoyaltyPoints points={rewardSeatPointsTotal} abbreviated />,
                cash: <FormatCurrency value={rewardPrice.cash} format="roundedDollar" />,
                b: (chunks: React.ReactNode) => <b>{chunks}</b>,
              }}
            />
          </Caption>
        )}
      </VerticalSpacer>
    )
  })() : (
    <>
      <LuxPlusPriceStack
        size="S"
        price={convertToDisplay(isFlightsSelected ? hotelPlusFlightsPrice : hotelPrice)}
        memberPrice={convertToDisplay(isFlightsSelected ? hotelPlusFlightsMemberPrice : hotelMemberPrice)}
        saleUnit={(priceMode === 'perPerson' ? 'person' : 'total') + (isFlightsSelected && hasFlights ? '*' : '')}
        testid="pricing"
        offerConfig={offer.luxPlus}
      />
      {isFlightsSelected && !flightPrice && <Caption variant="large">{intl.formatMessage({
        id: 'r2XFfIzqqb7Yt5tqkdfc',
        defaultMessage: '+ flights',
        description: 'Additional flights label when flight price is not yet available in multi-room footer',
      })}
      </Caption>}
      {shouldShowValue && <PriceRowValueDiscountWithCaption
        size="M"
        originalValue={convertToDisplay(isFlightsSelected ? hotelPlusFlightsValue : hotelValue)}
        discountPercentage={shouldShowDiscount ? discountPercentage : undefined}
      />}
    </>
  )

  return (
    <FooterContainer>
      <Group direction="vertical" gap={20} desktopDirection="horizontal-reverse" desktopHorizontalAlign="space-between" verticalAlign="center">
        <div>
          <Group direction="vertical" gap={20} desktopDirection="horizontal" verticalAlign="center">
            {canShowPrice && <div>
              <PriceRowPriceCaption>
                {isFlightsSelected && hasFlights ? nightsSaleUnitsFlightsFromCaption : nightsSaleUnitsFromCaption}
              </PriceRowPriceCaption>
              {rewardPriceElement}
            </div>}
            <div>

              {!shouldPreventMultipleRoomsBooking && <Group direction="vertical" gap={20}>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
                />
                {hasInsufficientRewardPoints && (
                  <InsufficientPointsBannerWrap>
                    <Sheet background="neutral-seven" padding={12}>
                      <BodyText variant="medium" colour="neutral-one">
                        <FormattedMessage
                          id="U_Lj6cAdjoL5AIMTwdC3"
                          defaultMessage="You require an additional {points} {loyaltyProgramName} points to continue to checkout. <link>Buy points now</link>"
                          description="Multi-room offer page footer guard shown when the member's Classic Rewards point balance is below the booking's redemption requirement; includes a link to the buy-points page."
                          values={{
                            points: <FormatLoyaltyPoints points={rewardPointsDeficit} />,
                            loyaltyProgramName: getLoyaltyProgramDisplayName(),
                            link: (chunks) => <TextLink size="medium" weight="bold" to="/societe/buy-points">{chunks}</TextLink>,
                          }}
                        />
                      </BodyText>
                    </Sheet>
                  </InsufficientPointsBannerWrap>
                )}
                {isFlightsSelected && <MultiRoomOfferSelectRoomsButton size="large" fit="flex" disabled={isBookNowDisabled || hasInsufficientRewardPoints} flightPrice={flightPrice} />}
                {!isFlightsSelected && <Group direction="horizontal" gap={12} horizontalAlign="stretch">
                  {checkIn && <MultiRoomOfferAddToCartButton size="large" fit="flex" disabled={isBookNowDisabled || hasInsufficientRewardPoints} />}
                  <MultiRoomOfferBookButton size="large" fit="flex" disabled={isBookNowDisabled || hasInsufficientRewardPoints} />
                </Group>}
              </Group>}
              {shouldPreventMultipleRoomsBooking && (
                <Sheet background="light-background" padding={20}>
                  <Group direction="vertical" gap={8}>
                    <Group direction="vertical" gap={4}>
                      <BodyText variant="medium" weight="bold">
                        {intl.formatMessage({
                          id: 'N9bY8xN-vdb3upPfjBcL',
                          defaultMessage: 'Ready to book? Call us to complete your booking',
                          description: 'Heading prompting user to call to complete multi-room booking',
                        })}
                      </BodyText>
                      <BodyText variant="medium">
                        {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? limitedHoursLabel : fullHoursLabel}
                      </BodyText>
                    </Group>
                    <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${currentContact.defaultContact.number}`} fit="flex">
                      {intl.formatMessage(
                        {
                          id: 'Y86IWbONeZPFFfPd9r7n',
                          defaultMessage: 'Call {phoneNumber}',
                          description: 'Action button label to call support with phone number',
                        },
                        { phoneNumber: currentContact.defaultContact.humanReadable },
                      )}
                    </TextButton>
                    {callbacksFeatureEnabled && (
                      <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
                        {intl.formatMessage({
                          id: 'B4gcrc2d3BbYkCRfnHQV',
                          defaultMessage: 'Request a callback',
                          description: 'Button text to request agent callback for multi-room booking',
                        })}
                      </TextButton>
                    )}
                  </Group>
                </Sheet>
              )}
            </div>
          </Group>
        </div>
        <div>
          {showBNBL && <OfferPageBNBLcta onOpenModal={onOpenBnblModal} direction="horizontal" desktopDirection="vertical" />}
        </div>
      </Group>
    </FooterContainer>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'checkIn'
  | 'isFlightsSelected'
  | 'multiRoomPackages'
  | 'multiRoomPackageTotals'
  | 'isLoadingByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'canRedeemLuxPlusBenefits'
  | 'baseBnblPackageViews'
  | 'priceMode'
  | 'priceIsLuxPlus'
  | 'charterBundleRoute'
  | 'duration'
  | 'fareClass'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  multiRoomPackages: getActivePackagesByRoom(state, appState),
  multiRoomPackageTotals: getMultiRoomPackageTotals(state, appState),
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  priceMode: state.priceMode,
  priceIsLuxPlus: getCanViewLuxPlusBenefits(appState),
  charterBundleRoute: state.charterBundleRoute,
  duration: state.duration,
  fareClass: state.fareClass,
}), MultipleRoomsIndividualRoomsFooter)
